import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import {
	type ArticleProductTypeFormSchema,
	articleProductTypeFormSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ArticleProductTypeForm } from "@/forms/article-product-type-form/article-product-type-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditArticleProductTypeModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	articleProductTypeId: string;
}

export function EditArticleProductTypeModal({
	onClose,
	isOpen,
	onComplete,
	articleProductTypeId,
}: EditArticleProductTypeModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["article-product-types"][":articleProductTypeId"].$get({
			param: { articleProductTypeId },
		});
		const data = await responseHandler(response);
		return articleProductTypeFormSchema.parse({
			...data.articleProductType,
			autoGenerateNumber: true,
		});
	});
	const methods = useForm({
		resolver: resolver(articleProductTypeFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: ArticleProductTypeFormSchema) {
		const response = await client.stock["article-product-types"][":articleProductTypeId"].$patch({
			param: { articleProductTypeId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_article_product_type")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ArticleProductTypeForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
